import {
  FullEngagementFragment,
  GetBansDocument,
  GetSubmissionsDocument,
  GetSubmissionsQuery,
  PhotoCarouselAdminConfig,
  PhotoCarouselAdminData,
  PhotoCarouselSubmissionData,
  PhotoCarouselViewConfig,
  PhotoCarouselViewData,
  useBanUserMutation,
  useDeleteSubmissionMutation,
  useGetBansQuery,
  useGetSubmissionsQuery,
  useOnSubmissionCreatedSubscription,
  useOnMockSubmissionCreatedSubscription,
  useUpdateSubmissionMutation,
} from '@/generated/graphql';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  TypedEngagement,
  asTypedEngagement,
  asTypedSubmissionQueryResult,
} from '@/utils/engagementTypeUtils';
import { ManageBansLink } from '@/components/bans/ManageBansLink';
import { engagementDefinitions } from '@/engagements';
import { useMockRealtimeChannel } from '@/hooks/useMockRealtimeChannel';
import { useBansEnabled } from '@/hooks/useBansEnabled';
import { generatePath } from 'react-router-dom';
import { SCREEN_MODERATION_ROUTE } from '@/routes/consts';
import useLocalStorage from 'use-local-storage';
import { toast } from 'sonner';
import { BanPersonDialog } from '@/components/bans/BanPersonDialog';
import { displaySubmitterName } from '@/utils/displaySubmitterName';
import {
  PHOTO_CAROUSEL_ADMIN_VIEW_STORAGE_KEY,
  PhotoCarouselAdminView,
  parsePhotoCarouselAdminView,
} from './adminViewPreference';
import {
  PhotoSubmissionFilter,
  PhotoSubmissionModel,
  getPhotoSubmissionStatus,
  matchesPhotoSubmissionFilter,
} from './submissionStatus';
import { PhotoCarouselViewToggle } from './SubmissionsToolbar';
import { PhotoSubmissionsPanel } from './PhotoSubmissionsPanel';
import { DeletePhotoDialog } from './PhotoSubmission';

function toModel(submission: {
  id: string;
  createdAt: string;
  userId?: string | null;
  user?: PhotoSubmissionModel['user'];
  possibleBannedDevice?: boolean | null;
  matchedBanCreatedAt?: string | null;
  data: PhotoCarouselSubmissionData;
}): PhotoSubmissionModel {
  return {
    id: submission.id,
    photoUrl: submission.data.photoUrl,
    caption: submission.data.caption,
    createdAt: submission.createdAt,
    approved: submission.data.approved,
    user: submission.user,
    userId: submission.userId,
    possibleBannedDevice: submission.possibleBannedDevice,
    matchedBanCreatedAt: submission.matchedBanCreatedAt,
  };
}

function PhotoCarouselAdminControlViewInner({
  engagement,
}: {
  engagement: TypedEngagement<
    PhotoCarouselAdminData,
    PhotoCarouselAdminConfig,
    PhotoCarouselViewData,
    PhotoCarouselViewConfig
  >;
}) {
  const { data: submissionsData } = useGetSubmissionsQuery({
    variables: { engagementId: engagement.id! },
    skip: !engagement.id,
  });

  const mockRealtime = useMockRealtimeChannel();
  const scrollRef = useRef<HTMLDivElement>(null);
  const newTimers = useRef<Map<string, number>>(new Map());
  const [newIds, setNewIds] = useState<Set<string>>(new Set());

  const markNew = (id: string) => {
    const scrolled = (scrollRef.current?.scrollTop ?? 0) > 8;
    setNewIds((prev) => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });
    const existing = newTimers.current.get(id);
    if (existing) window.clearTimeout(existing);
    const timeout = window.setTimeout(() => clearNew(id), 8000);
    newTimers.current.set(id, timeout);
    if (scrolled) {
      requestAnimationFrame(() => {
        if (scrollRef.current) {
          scrollRef.current.scrollTop += 64;
        }
      });
    }
  };

  const clearNew = (id: string) => {
    const existing = newTimers.current.get(id);
    if (existing) window.clearTimeout(existing);
    newTimers.current.delete(id);
    setNewIds((prev) => {
      if (!prev.has(id)) return prev;
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  };

  useEffect(() => {
    const timers = newTimers.current;
    return () => {
      for (const timeout of timers.values()) window.clearTimeout(timeout);
    };
  }, []);

  useOnSubmissionCreatedSubscription({
    variables: { engagementId: engagement.id! },
    skip: !engagement.id,
    onData: ({ data, client }) => {
      const created = data.data?.submissionCreated?.submission;
      client.cache.updateQuery<GetSubmissionsQuery>(
        {
          query: GetSubmissionsDocument,
          variables: { engagementId: engagement.id! },
        },
        (cachedData) => {
          if (!cachedData?.submissions || !created) return cachedData;
          return {
            submissions: [...cachedData.submissions, created],
          };
        },
      );
      if (created?.id) markNew(created.id);
    },
  });
  useOnMockSubmissionCreatedSubscription({
    variables: { engagementId: engagement.id! },
    skip: !engagement.id || !mockRealtime,
    onData: ({ data, client }) => {
      const created = data.data?.mockSubmissionCreated?.submission;
      client.cache.updateQuery<GetSubmissionsQuery>(
        {
          query: GetSubmissionsDocument,
          variables: { engagementId: engagement.id! },
        },
        (cachedData) => {
          if (!cachedData?.submissions || !created) return cachedData;
          return {
            submissions: [...cachedData.submissions, created],
          };
        },
      );
      if (created?.id) markNew(created.id);
    },
  });

  const typedSubmissionsData =
    asTypedSubmissionQueryResult<PhotoCarouselSubmissionData>(
      submissionsData || { submissions: [] },
    );

  const sorted = useMemo(() => {
    if (!typedSubmissionsData.submissions) return [];
    return typedSubmissionsData.submissions.slice().sort((a, b) => {
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });
  }, [typedSubmissionsData.submissions]);

  const models = useMemo(() => sorted.map(toModel), [sorted]);

  const [storedView, setStoredView] = useLocalStorage<PhotoCarouselAdminView>(
    PHOTO_CAROUSEL_ADMIN_VIEW_STORAGE_KEY,
    'cards',
  );
  const view = parsePhotoCarouselAdminView(storedView);
  const [filter, setFilter] = useState<PhotoSubmissionFilter>('all');
  const [previewId, setPreviewId] = useState<string | null>(null);
  const [moreMenuId, setMoreMenuId] = useState<string | null>(null);
  const [banTarget, setBanTarget] = useState<PhotoSubmissionModel | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<PhotoSubmissionModel | null>(
    null,
  );

  const counts = useMemo(() => {
    const next = { all: 0, pending: 0, approved: 0, rejected: 0 };
    for (const submission of models) {
      const status = getPhotoSubmissionStatus(submission.approved);
      next.all += 1;
      next[status] += 1;
    }
    return next;
  }, [models]);

  const visible = useMemo(
    () =>
      models.filter((submission) =>
        matchesPhotoSubmissionFilter(
          getPhotoSubmissionStatus(submission.approved),
          filter,
        ),
      ),
    [models, filter],
  );

  const engagementDef = engagementDefinitions[engagement.type];
  const { bansEnabled } = useBansEnabled();
  const { data: bansData } = useGetBansQuery({
    variables: { eventId: engagement.eventId },
    skip: !bansEnabled || !engagement.eventId,
  });
  const [updateSubmission] = useUpdateSubmissionMutation();
  const [deleteSubmission] = useDeleteSubmissionMutation();
  const [banUser, { loading: banLoading }] = useBanUserMutation();

  const handleApprove = (submission: PhotoSubmissionModel) => {
    updateSubmission({
      variables: {
        submissionId: submission.id,
        data: { approved: true },
      },
    });
  };

  const handleReject = (submission: PhotoSubmissionModel) => {
    updateSubmission({
      variables: {
        submissionId: submission.id,
        data: { approved: false },
      },
    });
  };

  const handleDelete = (submission: PhotoSubmissionModel) => {
    deleteSubmission({
      variables: { submissionId: submission.id },
      refetchQueries: [
        {
          query: GetSubmissionsDocument,
          variables: { engagementId: engagement.id! },
        },
      ],
    });
  };

  return (
    <div
      data-name="PHOTO_CAROUSEL_ADMIN_CONTROL_VIEW"
      className="flex h-full w-full items-start justify-start"
    >
      <div
        ref={scrollRef}
        className="flex w-full flex-col justify-start gap-4 overflow-auto p-4"
      >
        <div className="flex items-start justify-between gap-3">
          <h2 className="flex min-w-0 flex-row items-center gap-2 text-2xl font-bold">
            <engagementDef.Icon className="h-8 w-8 shrink-0" />
            <span className="truncate">{engagement.title}</span>
          </h2>
          <div className="flex shrink-0 items-center gap-2 lg:hidden">
            <PhotoCarouselViewToggle
              value={view}
              onChange={setStoredView}
              iconOnly
            />
          </div>
          {bansEnabled && (
            <div className="hidden pt-1 lg:block">
              <ManageBansLink
                count={bansData?.banCount ?? 0}
                to={generatePath(SCREEN_MODERATION_ROUTE, {
                  id: engagement.eventId,
                })}
              />
            </div>
          )}
        </div>
        {bansEnabled && (
          <div className="lg:hidden">
            <ManageBansLink
              count={bansData?.banCount ?? 0}
              to={generatePath(SCREEN_MODERATION_ROUTE, {
                id: engagement.eventId,
              })}
            />
          </div>
        )}
        <PhotoSubmissionsPanel
          view={view}
          onViewChange={setStoredView}
          filter={filter}
          onFilterChange={setFilter}
          counts={counts}
          submissions={visible}
          bansEnabled={bansEnabled}
          requireApproval={Boolean(engagement.config.requireApproval)}
          newIds={newIds}
          previewId={previewId}
          onPreviewIdChange={setPreviewId}
          moreMenuId={moreMenuId}
          onMoreMenuIdChange={setMoreMenuId}
          onApprove={handleApprove}
          onReject={handleReject}
          onRequestDelete={setDeleteTarget}
          onBan={setBanTarget}
          onInteract={clearNew}
        />
      </div>
      <DeletePhotoDialog
        open={Boolean(deleteTarget)}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null);
        }}
        onConfirm={() => {
          if (deleteTarget) handleDelete(deleteTarget);
        }}
      />
      {bansEnabled && banTarget?.user && (
        <BanPersonDialog
          open
          onOpenChange={(open) => {
            if (!open) setBanTarget(null);
          }}
          name={displaySubmitterName(banTarget.user, banTarget.userId).text}
          userId={banTarget.user.id ?? banTarget.userId ?? ''}
          photoUrl={banTarget.photoUrl}
          submissionCount={
            models.filter((item) => item.userId === banTarget.userId).length
          }
          loading={banLoading}
          onConfirm={async ({ hideExisting, reason }) => {
            const result = await banUser({
              variables: {
                submissionId: banTarget.id,
                hideExisting,
                reason: reason || undefined,
              },
              refetchQueries: [
                {
                  query: GetSubmissionsDocument,
                  variables: { engagementId: engagement.id! },
                },
                {
                  query: GetBansDocument,
                  variables: { eventId: engagement.eventId },
                },
              ],
            });
            const hidden = result.data?.banUser.hiddenCount ?? 0;
            const submitter = displaySubmitterName(
              banTarget.user,
              banTarget.userId,
            ).text;
            toast.success(
              hidden > 0
                ? `${submitter} banned · ${hidden} submissions hidden`
                : `${submitter} banned`,
            );
          }}
        />
      )}
    </div>
  );
}

export function PhotoCarouselAdminControlView({
  engagement,
}: {
  engagement: FullEngagementFragment;
}) {
  const typedEngagement = asTypedEngagement<
    PhotoCarouselAdminData,
    PhotoCarouselAdminConfig,
    PhotoCarouselViewData,
    PhotoCarouselViewConfig
  >(engagement);
  return <PhotoCarouselAdminControlViewInner engagement={typedEngagement} />;
}
